/**
 * Shared clamp that keeps a floating window's title bar reachable
 * (track:track-spec-window-escape-every-relay-window-closes-with-esc-and-no-window-can-be-dragged-out-of-reach-60046).
 *
 * The top edge may never leave the screen (the title bar carries the × and
 * the drag handle), and at least WINDOW_MIN_TITLE_VISIBLE px of the title bar
 * stay inside the screen horizontally.
 */
export const WINDOW_MIN_TITLE_VISIBLE = 120;
export const WINDOW_TITLE_BAR_CLEARANCE = 48;

export type WindowRect = { x: number; y: number; width: number; height: number };

export function clampWindow<T extends WindowRect>(
  rect: T,
  viewport: { width: number; height: number } = { width: window.innerWidth, height: window.innerHeight },
): T {
  const maxX = viewport.width - WINDOW_MIN_TITLE_VISIBLE;
  const minX = WINDOW_MIN_TITLE_VISIBLE - rect.width;
  const maxY = viewport.height - WINDOW_TITLE_BAR_CLEARANCE;
  const x = Math.min(Math.max(rect.x, Math.min(minX, maxX)), maxX);
  const y = Math.min(Math.max(rect.y, 0), Math.max(0, maxY));
  return x === rect.x && y === rect.y ? rect : { ...rect, x, y };
}

/** Centered rect for the given size (used by title-bar double-click). */
export function centerWindow(
  size: { width: number; height: number },
  viewport: { width: number; height: number } = { width: window.innerWidth, height: window.innerHeight },
): WindowRect {
  return clampWindow(
    {
      x: Math.round((viewport.width - size.width) / 2),
      y: Math.round((viewport.height - size.height) / 2),
      width: size.width,
      height: size.height,
    },
    viewport,
  );
}

/** True when a title-bar double-click landed on the bar itself, not a control inside it. */
export function isTitleBarTarget(target: EventTarget | null): boolean {
  return !(target instanceof Element && target.closest("button, a, input, select, textarea, [role='button']"));
}
